
import { useState } from 'react';
import { Youtube, Instagram, Facebook, Star, Play, Users, Heart, Eye, TrendingUp, Award } from 'lucide-react';
import { useIsMobile } from '../hooks/use-mobile';
import { YouTubeFacade } from './YouTubeFacade';

// Custom TikTok icon since it's not available in lucide-react
interface TikTokIconProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
}

const TikTokIcon = ({ size = 24, ...props }: TikTokIconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const SocialMedia = () => {
  const [activeTab, setActiveTab] = useState<'tiktok' | 'youtube'>('youtube');
  const isMobile = useIsMobile();

  return (
    <section id="social-media" className="py-16 md:py-24 bg-gradient-to-br from-blue-900 to-blue-950 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
      
      <div className="container-custom relative z-10">
        {/* Header Section with Trust Signals */}
        <div className="text-center max-w-3xl mx-auto mb-16 reveal-slide" style={{ '--reveal-delay': 0 } as React.CSSProperties}>
          <div className="inline-block px-4 py-2 mb-6 rounded-full bg-blue-100 text-blue-800 font-semibold text-sm border border-blue-200">
            🎥 Watch Our Team In Action
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
            See Why Colorado Trusts Us
          </h2>
          <p className="text-xl md:text-2xl font-semibold text-blue-200 leading-relaxed">
            Follow our social media to see 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-400"> real plumbing work</span>, learn professional tips, and watch our 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400"> master plumbers in action</span> across Colorado.
          </p>
          
          {/* Social Proof Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-neutral-white rounded-xl p-4 shadow-md border border-neutral-border hover:shadow-lg transition-all duration-300">
              <div className="flex items-center justify-center mb-2">
                <Youtube className="w-6 h-6 text-red-600" />
              </div>
              <div className="text-2xl font-bold text-authority-navy">50K+</div>
              <div className="text-sm text-neutral-gray">Views</div>
            </div>
            <div className="bg-neutral-white rounded-xl p-4 shadow-md border border-neutral-border hover:shadow-lg transition-all duration-300">
              <div className="flex items-center justify-center mb-2">
                <TikTokIcon size={24} className="text-black" />
              </div>
              <div className="text-2xl font-bold text-authority-navy">100K+</div>
              <div className="text-sm text-neutral-gray">Views</div>
            </div>
            <div className="bg-neutral-white rounded-xl p-4 shadow-md border border-neutral-border hover:shadow-lg transition-all duration-300">
              <div className="flex items-center justify-center mb-2">
                <Eye className="w-6 h-6 text-trust-primary" />
              </div>
              <div className="text-2xl font-bold text-authority-navy">200K+</div>
              <div className="text-sm text-neutral-gray">Social Views Combined</div>
            </div>
            <div className="bg-neutral-white rounded-xl p-4 shadow-md border border-neutral-border hover:shadow-lg transition-all duration-300">
              <div className="flex items-center justify-center mb-2">
                <Heart className="w-6 h-6 text-action-urgent" />
              </div>
              <div className="text-2xl font-bold text-authority-navy">25K+</div>
              <div className="text-sm text-neutral-gray">Likes</div>
            </div>
          </div>
        </div>
        
        {/* Main Video Content */}
        <div className="max-w-6xl mx-auto">
          {/* Platform Selector with Modern Design */}
          <div className="flex justify-center mb-12 reveal-slide" style={{ '--reveal-delay': 1 } as React.CSSProperties}>
            <div className="bg-neutral-white rounded-2xl p-2 shadow-xl border border-neutral-border inline-flex">
              <button
                className={`flex items-center px-8 py-4 rounded-xl transition-all duration-300 font-semibold ${
                  activeTab === 'youtube' 
                    ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg transform scale-105' 
                    : 'text-neutral-gray hover:text-red-600 hover:bg-red-50'
                }`}
                onClick={() => setActiveTab('youtube')}
              >
                <Youtube size={24} className="mr-3" />
                YouTube
              </button>
              <button
                className={`flex items-center px-8 py-4 rounded-xl transition-all duration-300 font-semibold ${
                  activeTab === 'tiktok' 
                    ? 'bg-gradient-to-r from-black to-gray-800 text-white shadow-lg transform scale-105' 
                    : 'text-neutral-gray hover:text-black hover:bg-gray-50'
                }`}
                onClick={() => setActiveTab('tiktok')}
              >
                <TikTokIcon size={24} className="mr-3" />
                TikTok
              </button>
            </div>
          </div>
          
          {/* Video Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {/* Main Video Card */}
            <div className="lg:col-span-2 reveal-slide" style={{ '--reveal-delay': 2 } as React.CSSProperties}>
              <div className="group bg-neutral-white rounded-2xl shadow-xl border border-neutral-border hover:shadow-2xl transition-all duration-500 overflow-hidden relative">
                {/* Gradient Border Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-trust-primary via-success-primary to-action-primary rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                <div className="absolute inset-[2px] bg-neutral-white rounded-2xl"></div>
                
                {/* Video Container */}
                <div className="relative z-10 p-6">
                  <div className="aspect-video w-full rounded-xl overflow-hidden shadow-lg">
                    {activeTab === 'youtube' ? (
                      <YouTubeFacade
                        videoId="q10OjQcrnuI"
                        title="Professional Sewer Repair Process"
                        autoplay={true}
                        muted={true}
                        loop={true}
                        className="w-full h-full"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full bg-black rounded-xl">
                        <div className="w-full max-w-[400px] mx-auto">
                          <iframe
                            width="100%"
                            height="100%"
                            src="https://www.tiktok.com/embed/v2/7405397966917258526?autoplay=1"
                            title="TikTok video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            className="aspect-video rounded-xl"
                          ></iframe>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Video Info */}
                  <div className="mt-6">
                    <h3 className="text-xl font-bold text-authority-navy mb-2">
                      {activeTab === 'youtube' ? 'Professional Sewer Repair Process' : 'Quick Plumbing Tips'}
                    </h3>
                    <p className="text-neutral-gray mb-4">
                      {activeTab === 'youtube' 
                        ? 'Watch our certified master plumbers demonstrate professional sewer line repair techniques and industry best practices.'
                        : 'Quick, actionable plumbing tips and tricks from Colorado\'s most trusted plumbers.'
                      }
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-neutral-gray">
                      <div className="flex items-center">
                        <Eye className="w-4 h-4 mr-1" />
                        {activeTab === 'youtube' ? '12.5K views' : '8.3K views'}
                      </div>
                      <div className="flex items-center">
                        <Heart className="w-4 h-4 mr-1" />
                        {activeTab === 'youtube' ? '340 likes' : '567 likes'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Side Features */}
            <div className="space-y-6 reveal-slide" style={{ '--reveal-delay': 3 } as React.CSSProperties}>
              {/* Trust Badge */}
              <div className="bg-gradient-to-br from-success-light via-trust-light to-success-light rounded-2xl p-6 border border-success-primary/20 shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-success-primary to-success-eco rounded-full flex items-center justify-center shadow-lg">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold text-authority-navy">Verified Experts</h4>
                    <p className="text-sm text-success-primary">Licensed Master Plumbers</p>
                  </div>
                </div>
                <p className="text-sm text-neutral-gray">
                  Watch real professionals with 300+ completed projects and 4.8★ rating.
                </p>
              </div>
              
              {/* Educational Content */}
              <div className="bg-gradient-to-br from-trust-light via-neutral-light to-trust-light rounded-2xl p-6 border border-trust-primary/20 shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-trust-primary to-trust-deep rounded-full flex items-center justify-center shadow-lg">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold text-authority-navy">Learn & Save</h4>
                    <p className="text-sm text-trust-primary">Pro Tips & Tutorials</p>
                  </div>
                </div>
                <p className="text-sm text-neutral-gray">
                  Get insider knowledge that could save you hundreds on plumbing repairs.
                </p>
              </div>
              
              {/* Behind the Scenes */}
              <div className="bg-gradient-to-br from-action-warm/10 via-neutral-light to-action-primary/10 rounded-2xl p-6 border border-action-primary/20 shadow-lg">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-action-primary to-action-urgent rounded-full flex items-center justify-center shadow-lg">
                    <Play className="w-6 h-6 text-white" />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold text-authority-navy">Behind the Scenes</h4>
                    <p className="text-sm text-action-primary">Real Job Footage</p>
                  </div>
                </div>
                <p className="text-sm text-neutral-gray">
                  See our team tackle real plumbing challenges across Colorado.
                </p>
              </div>
            </div>
          </div>
          
          {/* Social Media CTAs */}
          <div className="text-center reveal-slide" style={{ '--reveal-delay': 4 } as React.CSSProperties}>
            <div className="bg-gradient-to-r from-trust-deep to-authority-navy rounded-2xl p-8 text-white shadow-2xl border border-trust-primary/20 mb-8">
              <h3 className="text-2xl md:text-3xl font-bold mb-4">Stay Connected & Learn More</h3>
              <p className="text-lg mb-8 text-trust-light">
                Follow us for daily plumbing tips, behind-the-scenes content, and exclusive Colorado plumbing insights.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <a
                  href="https://www.tiktok.com/@milehighplumber"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-black hover:bg-gray-800 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                >
                  <div className="flex flex-col items-center">
                    <TikTokIcon size={32} className="mb-3 group-hover:animate-bounce" />
                    <span className="font-bold text-lg">TikTok</span>
                    <span className="text-sm opacity-80">@milehighplumber</span>
                    <div className="mt-2 text-xs bg-white/20 px-2 py-1 rounded-full">
                      Videos of Plumbing
                    </div>
                  </div>
                </a>
                
                <a
                  href="https://www.youtube.com/@bestmasterplumber"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                >
                  <div className="flex flex-col items-center">
                    <Youtube size={32} className="mb-3 group-hover:animate-bounce" />
                    <span className="font-bold text-lg">YouTube</span>
                    <span className="text-sm opacity-80">Subscribe Now</span>
                    <div className="mt-2 text-xs bg-white/20 px-2 py-1 rounded-full">
                      Videos of Projects
                    </div>
                  </div>
                </a>
                
                <a
                  href="https://www.facebook.com/coloradoplumbing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                >
                  <div className="flex flex-col items-center">
                    <Facebook size={32} className="mb-3 group-hover:animate-bounce" />
                    <span className="font-bold text-lg">Facebook</span>
                    <span className="text-sm opacity-80">Like Our Page</span>
                    <div className="mt-2 text-xs bg-white/20 px-2 py-1 rounded-full">
                      Local Updates
                    </div>
                  </div>
                </a>
                
                <a
                  href="https://www.instagram.com/coloradofirstplumbing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 hover:from-purple-600 hover:via-pink-600 hover:to-red-600 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                >
                  <div className="flex flex-col items-center">
                    <Instagram size={32} className="mb-3 group-hover:animate-bounce" />
                    <span className="font-bold text-lg">Instagram</span>
                    <span className="text-sm opacity-80">Follow Us</span>
                    <div className="mt-2 text-xs bg-white/20 px-2 py-1 rounded-full">
                      Daily Content
                    </div>
                  </div>
                </a>
              </div>
            </div>
            
            {/* Final CTA */}
            <div className="bg-gradient-to-r from-success-primary to-success-eco rounded-2xl p-6 text-white shadow-xl">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="mb-4 md:mb-0">
                  <h4 className="text-xl font-bold mb-2">Need Professional Plumbing Service?</h4>
                  <p className="text-success-light">Colorado's most trusted plumbers with 4.8★ rating</p>
                </div>
                <div className="flex space-x-4">
                  
                  <a 
                    href="/pricing" 
                    className="bg-action-primary hover:bg-action-urgent text-white px-6 py-3 rounded-xl font-bold transition-all duration-300 hover:scale-105 shadow-lg"
                  >
                    💰 See Pricing
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialMedia;
